// ─── Editing a container's route ─────────────────────────────────────────────
//
// The rules a form has to know before it offers a control: which stops can be
// moved, which can be removed, and where a new one is allowed to land. Every one
// of them is enforced by the API as well (`findRouteMutationViolation` in
// services/containerOrderService.js) — this exists so the page never offers a
// button whose click would be refused, which is a worse experience than not
// having the button.
//
// The line the rules are drawn on is the driver. Everything the container has
// already been to is settled; everything ahead of it is still the dispatcher's
// to arrange.

import { completedStepCount, lineSteps } from "./containerLineSteps";
import type {
  ContainerRouteStop,
  ContainerStopKind,
  ContainerStopRole,
} from "../types/containerOrders";

/** The stops whose place in the route the driver has already fixed. */
export function settledStopIds(
  stops: ContainerRouteStop[],
): ReadonlySet<string> {
  const line = { routeStops: stops };
  const done = completedStepCount(line);
  const steps = lineSteps(line);

  const ids = new Set<string>();
  for (const { stopIndex } of steps.slice(0, done)) {
    const id = stops[stopIndex]?._id;
    if (id) ids.add(String(id));
  }
  return ids;
}

/** Whether the driver has stamped either half of this stop. */
export const isStopVisited = (stop: ContainerRouteStop): boolean =>
  !!stop.arrivedAt || !!stop.completedAt;

const idOf = (stop: ContainerRouteStop) => String(stop._id ?? stop.role ?? "");

/**
 * The position of the last stop the container has actually been to.
 *
 * −1 when nothing has been driven, which is the case where the whole route is
 * still a plan and may be rearranged freely.
 */
export function lastSettledPosition(stops: ContainerRouteStop[]): number {
  const settled = settledStopIds(stops);
  let last = -1;
  stops.forEach((stop, index) => {
    if (settled.has(idOf(stop))) last = index;
  });
  return last;
}

/**
 * Whether this stop is in front of the driver in a way that matters.
 *
 * A stop that is **not being used** is exempt wherever it sits. It produces no
 * steps, so it cannot be a step nobody recorded, and its position is simply not
 * part of the run — an unused pickup terminal parked between the loading site
 * and the drop-off is an ordinary shape, not a problem to be locked down. That
 * exemption is the whole of the fix this mirrors: treating those stops as
 * settled made a container impossible to save at all, and told the user the
 * driver had been to a stop nobody was ever going to visit.
 */
export const isStopBehindDriver = (
  stops: ContainerRouteStop[],
  index: number,
): boolean => {
  const stop = stops[index];
  if (!stop || stop.enabled === false) return false;
  if (isStopVisited(stop)) return false;
  return index < lastSettledPosition(stops);
};

/**
 * Whether a proposed arrangement is one the API will accept.
 *
 * The client-side twin of `findRouteMutationViolation`, minus the messages: two
 * statements, checked separately, because they are two different things.
 *
 *   · the stops already driven still read in the order they were driven
 *   · nothing that *will* produce a step sits in front of them
 *
 * Checking the arrangement rather than the individual move is what keeps this
 * honest as the rules grow: a control is offered exactly when the result of
 * pressing it would be accepted, which is the only definition that cannot drift.
 */
export function isArrangementValid(
  original: ContainerRouteStop[],
  next: ContainerRouteStop[],
): boolean {
  const settled = settledStopIds(original);
  if (!settled.size) return true;

  const drivenOrder = original.filter((s) => settled.has(idOf(s))).map(idOf);
  const proposedOrder = next.filter((s) => settled.has(idOf(s))).map(idOf);
  if (proposedOrder.join("|") !== drivenOrder.join("|")) return false;

  let lastSettled = -1;
  next.forEach((stop, index) => {
    if (settled.has(idOf(stop))) lastSettled = index;
  });

  for (let index = 0; index < lastSettled; index += 1) {
    const stop = next[index];
    if (stop.enabled === false) continue;
    if (settled.has(idOf(stop))) continue;
    return false;
  }

  return true;
}

/**
 * The route with one stop moved by one place, or the route unchanged.
 *
 * One place at a time rather than drag-and-drop: a container route is at most a
 * dozen stops and usually four, the arrows work on a phone and with a keyboard,
 * and there is nothing to learn. Returning the route unchanged when the move is
 * not allowed means the caller can ask "would this do anything?" simply by
 * calling it — which is how the arrows decide whether to appear.
 */
export function moveStop(
  stops: ContainerRouteStop[],
  index: number,
  direction: -1 | 1,
): ContainerRouteStop[] {
  const target = index + direction;
  if (target < 0 || target >= stops.length) return stops;

  // A stop the container has been to is fixed where it is.
  if (isStopVisited(stops[index])) return stops;

  const next = [...stops];
  [next[index], next[target]] = [next[target], next[index]];

  return isArrangementValid(stops, next) ? next : stops;
}

/** Whether the arrows should be offered for this stop, per direction. */
export const canMoveStop = (
  stops: ContainerRouteStop[],
  index: number,
  direction: -1 | 1,
): boolean => moveStop(stops, index, direction) !== stops;

/** A fresh stop, ready to be filled in. */
export function makeCustomStop(kind: ContainerStopKind = "other"): ContainerRouteStop {
  return {
    /**
     * A client-side id, replaced by the server's on the first save.
     *
     * The route is sent whole and each stop is matched to its stored self by
     * this field, so a new stop needs one before it has ever been saved — two
     * unsaved stops added in the same sitting have to be distinguishable from
     * each other, and from every stop already on the route. The `new:` prefix is
     * what tells the merge on the server that there is nothing to match: an
     * ObjectId-shaped value would be looked up and silently found to be nothing.
     */
    _id: `new:${Math.random().toString(36).slice(2, 10)}`,
    role: "custom",
    kind,
    enabled: true,
    label: "",
    comment: "",
    companyName: "",
    address: {
      street: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
      coordinates: null,
    },
    contactName: "",
    contactPhone: "",
    contactEmail: "",
    ramp: "",
    bookingRef: "",
    pin: "",
    date: "",
    time: "",
  };
}

/**
 * Whether this id is one the database actually issued.
 *
 * Two kinds of id reach this module that the server has never stored: the
 * `new:` ones minted above for a stop just added, and the *role* the API puts
 * in the field for a container written before routes could vary, which has
 * never had stop ids at all. Neither survives the trip back — both are
 * recognised here by the one thing that distinguishes a real one, its shape.
 */
const STORED_ID = /^[0-9a-fA-F]{24}$/;

export const isStoredStopId = (id: string | undefined): boolean =>
  STORED_ID.test(String(id ?? ""));

/**
 * The route as the API wants it.
 *
 * Two things happen on the way out. Dates go from "" to null — an empty box
 * clears a date, and "" is not something a date field can be cast from — and any
 * id the database did not issue is dropped, because the server matches a sent
 * stop to its stored self by `_id` and would otherwise go looking for one that
 * does not exist. A stop arriving without an id is matched by its role instead,
 * which is exactly right for the two cases that produce one: a brand-new stop
 * (no role to collide with, so it is treated as new) and an anchor on a
 * container that predates stored routes.
 */
export function routeStopsPayload(
  stops: ContainerRouteStop[],
): ContainerRouteStop[] {
  return stops.map((stop, position) => {
    const { _id, ...rest } = stop;
    return {
      ...rest,
      ...(isStoredStopId(_id) ? { _id } : {}),
      position,
      date: stop.date || null,
      address: {
        ...(stop.address ?? {}),
        coordinates: stop.address?.coordinates ?? null,
      },
    } as ContainerRouteStop;
  });
}

/**
 * Which slot labels a stop's date and time are shown under.
 *
 * Each anchor names its slot after what happens there — collection, the visit to
 * site, the return — rather than all of them being called "date", so a card can
 * be read on its own. An added stop has no such name; "at this stop" is what it
 * is, and inventing one per kind would produce four vocabularies for one field.
 */
export const STOP_SLOT_KEYS: Record<
  ContainerStopRole,
  { ref: string; date: string; time: string }
> = {
  terminalPickup: {
    ref: "route.pickupRef",
    date: "route.pickupDate",
    time: "route.pickupTime",
  },
  loadingSite: {
    ref: "route.loadingSiteRef",
    date: "route.onSiteDate",
    time: "route.onSiteTime",
  },
  terminalDropoff: {
    ref: "route.deliveryRef",
    date: "route.deliveryDate",
    time: "route.deliveryTime",
  },
  custom: {
    ref: "route.stopRef",
    date: "route.stopDate",
    time: "route.stopTime",
  },
};
